import { Col, Modal, Row, Spin, Statistic, Typography } from "antd";
import { useEffect, useState } from "react";
import { getQuestionnaireAnswerResults } from "../repository/data.repository";
import {
  MAX_PAGE_SIZE,
  QuestionnaireAnswerKPIResult,
} from "../repository/data.default";
import { QueryOperation } from "../code/search-query/search-query";
import { groupByField } from "./analytics/AnalyticsRange";
import { renderMetricChart } from "./analytics/MetricChartRender";

// Per-asset psychometric snapshot: the KPI results historically attributed to this asset (via the answer
// snapshot's AssetUid) — crew assessed, total scored results, average score + a result-band distribution.
// Ships with no backend change: getQuestionnaireAnswerResults is already filterable by assetUid.
export interface AssetMetricsViewProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly assetUid?: string;
  readonly assetName?: string;
}

function AssetMetricsView(props: AssetMetricsViewProps) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<QuestionnaireAnswerKPIResult[]>([]);

  useEffect(() => {
    if (!props.open || !props.assetUid) return;
    let cancelled = false;
    setLoading(true);
    getQuestionnaireAnswerResults({
      searchQuery: {
        conditions: [
          { field: "assetUid", operation: QueryOperation.Equal, values: [props.assetUid] },
        ],
      },
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
    })
      .then(({ data }) => {
        if (!cancelled) {
          setResults(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResults([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [props.open, props.assetUid]);

  const bands = groupByField(results, "resultingRangeLabel");
  const avgScore = results.length
    ? Math.round(
        (results.reduce((s, r) => s + (r.score ?? 0), 0) / results.length) * 100,
      ) / 100
    : 0;
  const crew = new Set(results.map((r) => r.userUid).filter(Boolean)).size;

  return (
    <Modal
      open={props.open}
      onCancel={props.onClose}
      footer={null}
      width={720}
      destroyOnClose
      title={`Asset metrics — ${props.assetName ?? ""}`}
    >
      <Spin spinning={loading}>
        <Row gutter={24} className="mb-3">
          <Col>
            <Statistic title="Crew assessed" value={crew} />
          </Col>
          <Col>
            <Statistic title="Scored results" value={results.length} />
          </Col>
          <Col>
            <Statistic title="Avg. score" value={avgScore} />
          </Col>
        </Row>
        <Typography.Text type="secondary">
          Result-band distribution across this asset's crew assessments (attributed to the
          asset they were on at submit time).
        </Typography.Text>
        <div className="mt-2">
          {renderMetricChart("ring", {}, bands, "Results", 280)}
        </div>
      </Spin>
    </Modal>
  );
}

export default AssetMetricsView;
